import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { showMessage } from '~/actions';
import { logCustomEvent } from '~/api/analytics';
import { fetchNpsPrompt } from '~/domain/NpsReview';
import { showErrorMessage } from '~/domain/utils';
import { routes } from '~/routes';
import { apiMethods } from '~/services/api';
import { apiMethodsConst } from '~/services/methods';
import { getShouldUseNpsIdentity } from '~/services/posthog/FeatureFlagUtils';
import { store } from '~/store/configureStore';
import { getErrorMessage } from '~/utils/ErrorUtils';
import type { ISaveShiftReviewPayload } from '../Types';

type TSaveShiftReviewResponse = unknown;
type TSaveShiftReviewError = AxiosError<{ message?: string; error?: string; title?: string }>;

const shiftReviewMutationKeys = {
  save: (jobId: string) => ['saveShiftReview', jobId] as const,
};

/**
 * Whether to open the NPS screen after a review. Flag off: the monolith's
 * `show_nps` on the signed-in user. Flag on: auth-service decides, and a failed
 * call means "don't show", so the review flow never breaks on it (GOTU-16475).
 */
const shouldShowNps = async (): Promise<boolean> => {
  if (!getShouldUseNpsIdentity()) {
    return Boolean(store.getState().user.user?.show_nps);
  }

  try {
    const prompt = await fetchNpsPrompt();
    return prompt?.show === true;
  } catch {
    return false;
  }
};

/**
 * The review's success dialog, whose Ok opens the NPS screen when it is due.
 * Exported for a flow that saved silently and shows the dialog once it ends.
 */
export const showShiftReviewSentMessage = async (): Promise<void> => {
  const isNpsDue = await shouldShowNps();

  store.dispatch(
    showMessage({
      message: 'Your feedback has been saved. Thank you for helping us improve your experience.',
      title: 'Feedback Successfully Sent!',
      route: isNpsDue ? routes.npsReview : routes.dashboardHome,
      isSuccess: true,
      modalType: 'feedbackSuccess', // Used for stable test selectors
    }),
  );
};

/**
 * Saves the office's end-of-day review of the professional. The success
 * dialog is awaited inside `onSuccess`, so the mutation stays pending — and
 * Send stays disabled — until the dialog's route is known. A failure shows
 * "Sorry!" and leaves the next attempt to the office.
 */
export function useSaveShiftReview(jobId: string) {
  const queryClient = useQueryClient();

  return useMutation<TSaveShiftReviewResponse, TSaveShiftReviewError, ISaveShiftReviewPayload>({
    mutationKey: shiftReviewMutationKeys.save(jobId),
    mutationFn: async ({ data }) => {
      return await apiMethods[apiMethodsConst.FEEDBACK_SAVE_REQUEST]({ data, jobId });
    },
    onSuccess: async (_response, { data, showMessage: shouldShowMessage = true }) => {
      void logCustomEvent('send_review', { ...data, jobId });
      queryClient.invalidateQueries({ queryKey: ['getShiftPendingReviews'] });

      if (shouldShowMessage) {
        await showShiftReviewSentMessage();
      }
    },
    onError: (error, { showMessage: shouldShowMessage = true }) => {
      if (!shouldShowMessage) {
        // The flow that saved silently raises its own message (Invite → "Request(s)
        // Successfully Sent!"), which would replace "Sorry!" in the one message slot.
        // errorReducer turns any `*_FAILURE` into the separate ErrorShower dialog.
        store.dispatch({
          type: 'SAVE_SHIFT_REVIEW_FAILURE',
          payload: { message: getErrorMessage(error) },
        });
        return;
      }

      showErrorMessage({ error, title: 'Sorry!' });
    },
  });
}

/**
 * True once a save for this job has succeeded in this session. Read from the
 * mutation cache, so it survives the rating screen remounting (an eligible-job
 * card press and Back restores the invite popup on a fresh screen) for as long
 * as React Query keeps the settled mutation.
 */
export function useHasSavedShiftReview(jobId: string): boolean {
  const savedAt = useMutationState<number>({
    filters: { mutationKey: shiftReviewMutationKeys.save(jobId), status: 'success' },
    select: (mutation) => mutation.state.submittedAt,
  });

  return savedAt.length > 0;
}
